import type { Maybe } from '@adornis/base/utilTypes.js';
import { todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { BillingRhythm, isDynamicPriceInCents } from '../../billingPositions/entities/BillingPosition.js';
import { collectsNothing, type MandateRuntime, RUNS_ON_SOME_DAY_SELECTOR, runsOnNoDay } from './mandateActivity.js';

/**
 * Where a mandate is in its life. This is the one answer every component asks for - the person's
 * list, the card, the group views, bulk accept, the booking hooks - so they never disagree about
 * which mandate covers a booking. Whether it collects money (`isFunded`) and whether its group is
 * in billing (`billingLocked`) are properties on top, not phases.
 */
export enum MandatePhase {
  /**
   * No consent: a generated template, an unaccepted draft, or an ended row of the booking offered
   * again (`findReusableMandate`) - accepting it takes the row over with its Mandatsreferenz.
   */
  TEMPLATE = 'TEMPLATE',
  PLANNED = 'PLANNED',
  RUNNING = 'RUNNING',
  ENDED = 'ENDED',
  /** A one-time mandate a lock export collected: done for good, never offered again. */
  BILLED = 'BILLED',
}

/** The phases in which a mandate covers its booking: no template is offered for it. */
export const LIVE_PHASES: MandatePhase[] = [MandatePhase.PLANNED, MandatePhase.RUNNING];

export interface MandatePhaseSubject extends Partial<MandateRuntime> {
  accepted?: Maybe<boolean>;
  rhythm?: Maybe<BillingRhythm>;
  billedOn?: Maybe<Date>;
}

/**
 * `day` and the runtime are canonical calendar days (`@adornis/datetime/calendarDay.js`), so this
 * is a plain instant comparison.
 */
export function getMandatePhase(mandate: MandatePhaseSubject, day = todayAsCalendarDay()): MandatePhase {
  if (!mandate.accepted) return MandatePhase.TEMPLATE;
  /**
   * The export ends a one-time mandate on the Stichtag that collected it, so its runtime alone would
   * read like a withdrawal. Monthly and yearly mandates carry `billedOn` after every collection and
   * keep running, so the marker only decides for one-time mandates.
   */
  if (mandate.rhythm === BillingRhythm.ONCE_CUSTOM && mandate.billedOn) return MandatePhase.BILLED;
  // ending a planned mandate leaves its end the day before its start: it never runs, so it is over
  if (runsOnNoDay(mandate) || (mandate.end && mandate.end < day)) return MandatePhase.ENDED;
  if (mandate.start && mandate.start > day) return MandatePhase.PLANNED;

  return MandatePhase.RUNNING;
}

const NOT_BILLED_ONCE = { $nor: [{ rhythm: BillingRhythm.ONCE_CUSTOM, billedOn: { $ne: null } }] };

const PHASE_SELECTORS = {
  [MandatePhase.PLANNED]: (day: Date) => ({
    accepted: true,
    start: { $gt: day },
    $and: [RUNS_ON_SOME_DAY_SELECTOR, NOT_BILLED_ONCE],
  }),
  [MandatePhase.RUNNING]: (day: Date) => ({
    accepted: true,
    $and: [
      { $or: [{ start: null }, { start: { $lte: day } }] },
      { $or: [{ end: null }, { end: { $gte: day } }] },
      NOT_BILLED_ONCE,
    ],
  }),
  [MandatePhase.BILLED]: () => ({ accepted: true, rhythm: BillingRhythm.ONCE_CUSTOM, billedOn: { $ne: null } }),
};

/**
 * Mongo counterpart of {@link getMandatePhase}, for the phases a query ever asks for. `null` also
 * matches a missing field.
 */
export function getMandatePhaseSelector(
  phases: Array<keyof typeof PHASE_SELECTORS>,
  day = todayAsCalendarDay(),
): Record<string, unknown> {
  return { $or: phases.map(phase => PHASE_SELECTORS[phase](day)) };
}

/**
 * Gefördert, befreit or pausiert: nothing is collected (`collectsNothing`). A dynamic price is no
 * funding - its negative amount is a placeholder filled in per usage, the mandate is a normal one.
 */
export function isFunded(mandate: { amountInCents?: Maybe<number> }): boolean {
  return !isDynamicPriceInCents(mandate.amountInCents) && collectsNothing(mandate);
}

/**
 * Whether a write gives consent: a new mandate, an accepted draft, or an ended row of the booking
 * taken over again. Every acceptance gets the same checks - overlap, billing lock, start - whichever
 * path it comes from (the card, bulk accept). `stored` is null on a create.
 */
export function isAcceptance(stored: Maybe<MandatePhaseSubject>, next: MandatePhaseSubject): boolean {
  if (!LIVE_PHASES.includes(getMandatePhase(next))) return false;
  if (!stored) return true;

  return [MandatePhase.TEMPLATE, MandatePhase.ENDED].includes(getMandatePhase(stored));
}

/**
 * The stored row a new consent for the booking takes over instead of drawing a new
 * Mandatsreferenz: a draft or an ended mandate of the same booking and billing position, accepted
 * by the same person - nobody consents in someone else's name, so another guardian gets a new
 * mandate. A billed one-time mandate is never taken over. The latest ended one wins.
 */
export function findReusableMandate<
  T extends MandatePhaseSubject & { bookingID?: Maybe<string>; name?: Maybe<string>; acceptedByID?: Maybe<string> },
>(mandates: T[], template: { bookingID?: Maybe<string>; name?: Maybe<string> }, acceptorID: string): T | null {
  const reusable = mandates.filter(
    mandate =>
      mandate.bookingID === template.bookingID &&
      mandate.name === template.name &&
      (!mandate.acceptedByID || mandate.acceptedByID === acceptorID) &&
      [MandatePhase.TEMPLATE, MandatePhase.ENDED].includes(getMandatePhase(mandate)),
  );

  return reusable.sort((a, b) => (b.end?.getTime() ?? 0) - (a.end?.getTime() ?? 0))[0] ?? null;
}
